import { Component } from '@theme/component';

/**
 * <nk-bundle-select>
 *
 * Wrapper um die bestehende <product-form-component> der Produktseite (nk-product-hero).
 * Rendert ankreuzbare Cross-Sell-Karten und fügt beim Klick auf den Haupt-Kaufbutton
 * das Hauptprodukt + alle angekreuzten E-Books in EINEM /cart/add.js-Call hinzu.
 *
 * Mechanik:
 * - Der Haupt-Button ist type="submit". Sein nativer Submit löst on:submit="/handleSubmit"
 *   in der ProductFormComponent aus (Einzel-Add).
 * - Wir fangen den Button-Click in der Capture-Phase ab. Sind Checkboxen angehakt,
 *   rufen wir event.preventDefault() auf -> der native Submit (und damit handleSubmit)
 *   läuft NICHT -> wir übernehmen mit addItemsBatch([Hauptprodukt + angekreuzte]).
 * - Ist nichts angehakt, lassen wir den normalen Einzel-Add unangetastet durchlaufen.
 * - Vor dem Hinzufügen wird der aktuelle Warenkorb (/cart.js) geprüft: bereits
 *   enthaltene Varianten (Hauptbuch ODER E-Books) werden NICHT erneut gesendet,
 *   damit nichts doppelt im Warenkorb landet.
 *
 * Bewusst KEINE requiredRefs, damit die Component nie wirft, falls sie ohne Karten
 * gerendert wird.
 */
class NkBundleSelect extends Component {
  /** @type {import('./product-form.js').ProductFormComponent | null} */
  #form = null;
  /** @type {HTMLFormElement | null} */
  #formEl = null;
  /** @type {HTMLButtonElement | null} */
  #button = null;

  connectedCallback() {
    super.connectedCallback();

    this.#form = /** @type {any} */ (this.querySelector('product-form-component'));
    this.#formEl = this.#form?.querySelector('form') ?? null;
    this.#button = this.#formEl?.querySelector('button[type="submit"][name="add"]') ?? null;

    this.#button?.addEventListener('click', this.#onButtonClick, { capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#button?.removeEventListener('click', this.#onButtonClick, { capture: true });
  }

  /** @param {MouseEvent} event */
  #onButtonClick = (event) => {
    const checked = this.#checkedItems();

    // Nichts angekreuzt -> normaler Einzel-Add (handleSubmit) übernimmt.
    if (checked.length === 0) return;

    const mainId = this.#mainVariantId();
    if (!mainId) return;

    // Verhindert den nativen Submit der Form -> handleSubmit läuft NICHT.
    // MUSS synchron passieren (vor jedem await), sonst submittet die Form schon.
    event.preventDefault();

    // Kandidaten: Hauptprodukt + angekreuzte E-Books.
    const candidates = [{ variantId: mainId, quantity: this.#mainQuantity() }, ...checked];

    // Warenkorb prüfen und bereits vorhandene Varianten herausfiltern,
    // damit nichts doppelt hinzugefügt wird.
    this.#addWithoutDuplicates(candidates);
  };

  /** @param {Array<{variantId: string, quantity: number}>} candidates */
  #addWithoutDuplicates = async (candidates) => {
    let existingIds = new Set();
    try {
      const res = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (res.ok) {
        const cart = await res.json();
        existingIds = new Set((cart.items || []).map((i) => String(i.variant_id)));
      }
    } catch (e) {
      // Bei Fehler lieber alles senden als gar nichts — Standardverhalten.
      existingIds = new Set();
    }

    const items = candidates.filter((item) => !existingIds.has(String(item.variantId)));

    // Alles schon im Warenkorb -> nur den Drawer öffnen, nichts hinzufügen.
    if (items.length === 0) {
      this.#openCartDrawer();
      return;
    }

    if (typeof this.#form?.addItemsBatch === 'function') {
      this.#form.addItemsBatch(items);
    }
  };

  /** Öffnet den Cart-Drawer (falls vorhanden), wenn nichts hinzuzufügen war. */
  #openCartDrawer() {
    const drawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
    if (drawer && drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  /** @returns {Array<{variantId: string, quantity: number}>} */
  #checkedItems() {
    const boxes = this.querySelectorAll('.nk-php__bundle-check input[type="checkbox"]');

    return Array.from(boxes)
      .filter((b) => b instanceof HTMLInputElement && b.checked && b.value)
      .map((b) => ({ variantId: /** @type {HTMLInputElement} */ (b).value, quantity: 1 }));
  }

  /** @returns {string | null} */
  #mainVariantId() {
    const input = this.#formEl?.querySelector('input[name="id"]');
    return input instanceof HTMLInputElement && input.value ? input.value : null;
  }

  /** @returns {number} */
  #mainQuantity() {
    return Number(this.#form?.dataset.quantityDefault) || 1;
  }
}

if (!customElements.get('nk-bundle-select')) {
  customElements.define('nk-bundle-select', NkBundleSelect);
}
